// Upload one video file to the Media Library and return its asset row - the same three steps the
// Media Library page uses (presign, direct PUT to R2, register with a poster frame), so a video
// dropped straight onto a post composer is stored, thumbnailed and classified exactly like one
// uploaded on the Library page. Rebecca, Oct 25, 2026: "Everything has to go into the media
// library first before you can use it, which adds extra steps."
//
// ⚠️ A POSTER FRAME NEEDS THE BROWSER TO DECODE THE VIDEO, and phone HEVC/ProRes files often can't
// be decoded. That must never block the upload: past 20 seconds, or on an error, it uploads with no
// poster and the server stores a placeholder thumbnail (same rule as MediaLibrary.js).
// sha256Hex and videoPoster mirror the copies in MediaLibrary.js; change one, change both.

const UPLOAD_URL = '/.netlify/functions/media-library-upload';

async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function videoPoster(file) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true; video.playsInline = true; video.preload = 'metadata'; video.src = url;
    await new Promise((resolve, reject) => {
      video.onloadeddata = resolve;
      video.onerror = () => reject(new Error('unsupported codec'));
      setTimeout(() => reject(new Error('timed out')), 12000);
    });
    const target = Math.min(1, (video.duration || 2) / 3);
    await new Promise((resolve) => { video.onseeked = resolve; try { video.currentTime = target; } catch { resolve(); } });
    const w = video.videoWidth, h = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w || 640; canvas.height = h || 360;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const jpeg = canvas.toDataURL('image/jpeg', 0.82);
    return { posterBase64: jpeg.slice(jpeg.indexOf(',') + 1), duration: isFinite(video.duration) ? Math.round(video.duration) : null, width: w || null, height: h || null };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const isVideoFile = (f) => !!f && (/^video\//.test(f.type) || /\.(mp4|mov|m4v|webm)$/i.test(f.name || ''));

/**
 * @param file      a File from a drop or a file input
 * @param opts      { brand: 'crab-place' | 'crab-and-cruise', adminId, onStatus(text) }
 * @returns the Media Library asset row (r2_original_key, r2_thumb_key, media_kind 'video', ...)
 */
export async function uploadVideoToLibrary(file, { brand, adminId, onStatus } = {}) {
  const say = (t) => { if (onStatus) onStatus(t); };
  say('reading video…');
  const sha = await sha256Hex(file);
  let poster = { posterBase64: null, duration: null, width: null, height: null };
  try {
    poster = await Promise.race([videoPoster(file), new Promise((resolve) => setTimeout(() => resolve(poster), 20000))]);
  } catch { /* undecodable codec: upload anyway, no poster frame */ }
  const ext = ((file.name || '').match(/\.([a-z0-9]+)$/i) || [, 'mp4'])[1].toLowerCase();
  const pres = await fetch(UPLOAD_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'presign', sha, ext }) });
  const presData = await pres.json().catch(() => ({}));
  if (!pres.ok) throw new Error(presData.error || `could not start the upload (${pres.status})`);
  say('uploading video…');
  const put = await fetch(presData.uploadUrl, { method: 'PUT', headers: { 'Content-Type': presData.contentType }, body: file });
  if (!put.ok) throw new Error(`upload failed (${put.status})`);
  say('saving to the Media Library…');
  const reg = await fetch(UPLOAD_URL, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'register-video', sha, key: presData.key, contentType: presData.contentType, filename: file.name,
      posterBase64: poster.posterBase64, duration: poster.duration, width: poster.width, height: poster.height,
      brand: brand || 'crab-place', admin_id: adminId,
    }),
  });
  const regData = await reg.json().catch(() => ({}));
  if (!reg.ok) throw new Error(regData.error || `could not save it (${reg.status})`);
  return regData.row || regData;
}
